import {
  type Configuration,
  PublicClientApplication,
  type RedirectRequest,
} from '@azure/msal-browser';

/**
 * MSAL.js config for the Admin Console SPA. Values come from build-time
 * Vite env vars (`VITE_MSAL_*`) so each deployed env (dev / stage / uat /
 * prod) pins its own Entra app registration without code changes.
 *
 * Defaults are intentionally placeholders so the SPA still mounts in local
 * dev when the operator hasn't populated `.env.local` yet — clicking
 * "Sign in" without real values fails at the redirect step (Entra returns
 * an error), which is the right local-dev behavior.
 */
const PLACEHOLDER_CLIENT_ID = '00000000-0000-0000-0000-000000000000';
const PLACEHOLDER_AUTHORITY = 'https://login.microsoftonline.com/common';

export interface MsalConfigInputs {
  clientId?: string;
  authority?: string;
  redirectUri?: string;
}

export function buildMsalConfig(inputs: MsalConfigInputs = {}): Configuration {
  return {
    auth: {
      clientId: inputs.clientId ?? PLACEHOLDER_CLIENT_ID,
      authority: inputs.authority ?? PLACEHOLDER_AUTHORITY,
      redirectUri: inputs.redirectUri ?? '/',
      postLogoutRedirectUri: '/',
      navigateToLoginRequestUrl: true,
    },
    cache: {
      // sessionStorage keeps tokens out of localStorage (lower XSS blast
      // radius) at the cost of losing them on tab close — acceptable for
      // an internal Admin Console used by operators in active sessions.
      cacheLocation: 'sessionStorage',
      storeAuthStateInCookie: false,
    },
  };
}

/**
 * Build the {@link PublicClientApplication} from Vite's import.meta.env.
 * Window-bound `redirectUri` is set lazily so this stays SSR-safe in tests
 * (jsdom provides `window.location.origin`; node-env tests don't import
 * this file).
 */
export function createMsalInstance(): PublicClientApplication {
  return new PublicClientApplication(
    buildMsalConfig({
      clientId: import.meta.env.VITE_MSAL_CLIENT_ID,
      authority: import.meta.env.VITE_MSAL_AUTHORITY,
      redirectUri: typeof window === 'undefined' ? undefined : window.location.origin,
    }),
  );
}

/** Default scopes requested at sign-in. The API audience comes from env. */
export function buildLoginRequest(): RedirectRequest {
  const apiAudience = import.meta.env.VITE_MSAL_API_AUDIENCE;
  const scopes = apiAudience ? [`${apiAudience}/.default`] : ['openid', 'profile'];
  return { scopes };
}
